import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { DeveloperDashboardService } from '../../../services/developer-dashboard.service';
import { DeveloperTabsService } from '../../../services/developer-tabs.service';
import { RentTierPayload, SalePlanPayload } from '../../../../../../shared/utils/form-data-builder';
import { DeveloperProject } from '../../../models/developer-dashboard.model';
import { AddNewProperty } from "../../../../add-new-property/add-new-property";

type Step = 'common' | 'price' | 'media';

@Component({
  selector: 'app-create-unit',
  imports: [CommonModule, FormsModule, AddNewProperty],
  templateUrl: './create-unit.html',
  styleUrl: './create-unit.scss',
})
export class CreateUnit implements OnInit {
  step: Step = 'common';
  isSaving = false;
  saveError = '';
  saveSuccess = '';

  // ── Projects ──────────────────────────────────────────────
  projects: DeveloperProject[] = [];
  projectId = '';
  loadingProjects = false;

  // ── Common ────────────────────────────────────────────────
  title_en = '';
  title_ar = '';
  description_en = '';
  description_ar = '';
  unitNumber = '';
  unitType_en = 'apartment';
  purpose = 'rent';
  buildYear: number | null = null;
  unitArea: number | null = null;
  bedrooms = 1;
  bathrooms = 1;
  floorNumber: number | null = null;
  furnished = true;
  maxGuests = 1;
  country_en = 'Egypt';
  country_ar = 'مصر';
  city_en = '';
  city_ar = '';
  area_en = '';
  area_ar = '';
  address_en = '';
  address_ar = '';
  lng: number | null = null;
  lat: number | null = null;
  facilitiesAndServices_en: string[] = [];
  facilitiesAndServices_ar: string[] = [];
  utilities_en: string[] = ['electricity', 'water'];
  utilities_ar: string[] = ['كهرباء', 'مياه'];
  status = 'active';

  // ── Price ─────────────────────────────────────────────────
  rentPrice: number | null = null;
  rentCurrency = 'EGP';
  securityDeposit: number | null = null;
  rentTiers: RentTierPayload[] = [
    { minDays: 1, pricePerDay: null, label: 'Standard (1+ day)' },
    { minDays: 7, pricePerDay: null, label: 'Weekly deal (7+ days)' },
    { minDays: 30, pricePerDay: null, label: 'Monthly deal (30+ days)' },
  ];
  basePrice: number | null = null;
  pricePerMeter: number | null = null;
  saleCurrency = 'EGP';
  negotiable = false;
  salePlans: SalePlanPayload[] = [];

  // ── Media ─────────────────────────────────────────────────
  galleryFiles: File[] = [];
  documentFiles: File[] = [];
  galleryPreviews: string[] = [];

  readonly steps: { key: Step; label: string; icon: string }[] = [
    { key: 'common', label: 'developerDashboard.createUnit.stepData', icon: 'fa-pen' },
    { key: 'price', label: 'developerDashboard.createUnit.stepPrice', icon: 'fa-tag' },
    { key: 'media', label: 'developerDashboard.createUnit.stepMedia', icon: 'fa-images' },
  ];

  readonly unitTypes = [
    { key: 'apartment', label_en: 'Apartment', label_ar: 'شقة' },
    { key: 'villa', label_en: 'Villa', label_ar: 'فيلا' },
    { key: 'studio', label_en: 'Studio', label_ar: 'استوديو' },
    { key: 'shop', label_en: 'Shop', label_ar: 'متجر' },
    { key: 'office', label_en: 'Office', label_ar: 'مكتب' },
  ];

  readonly availableFacilities = [
    { key_en: 'parking', key_ar: 'موقف سيارات' },
    { key_en: 'elevator', key_ar: 'مصعد' },
    { key_en: 'security', key_ar: 'حراسة' },
    { key_en: 'gym', key_ar: 'صالة رياضية' },
    { key_en: 'pool', key_ar: 'حمام سباحة' },
    { key_en: 'garden', key_ar: 'حديقة' },
    { key_en: 'balcony', key_ar: 'شرفة' },
    { key_en: 'air conditioning', key_ar: 'تكييف هواء' },
  ];

  constructor(
    private service: DeveloperDashboardService,
    private tabsService: DeveloperTabsService,
  ) { }

  ngOnInit(): void {
    this.loadingProjects = true;
    this.service.getProjects().subscribe({
      next: (projects) => {
        this.projects = projects;
        if (projects.length === 1) this.projectId = projects[0]._id;
        this.loadingProjects = false;
      },
      error: () => {
        this.loadingProjects = false;
      },
    });
  }

  setStep(s: Step): void {
    this.step = s;
    this.saveError = '';
    this.saveSuccess = '';
  }

  get showRent(): boolean {
    return this.purpose === 'rent' || this.purpose === 'sale_and_rent';
  }
  get showSale(): boolean {
    return this.purpose === 'sale' || this.purpose === 'sale_and_rent';
  }

  isFacilitySelected(key_en: string): boolean {
    return this.facilitiesAndServices_en.includes(key_en);
  }

  toggleFacility(key_en: string, key_ar: string): void {
    if (this.isFacilitySelected(key_en)) {
      this.facilitiesAndServices_en = this.facilitiesAndServices_en.filter((f) => f !== key_en);
      this.facilitiesAndServices_ar = this.facilitiesAndServices_ar.filter((f) => f !== key_ar);
    } else {
      this.facilitiesAndServices_en = [...this.facilitiesAndServices_en, key_en];
      this.facilitiesAndServices_ar = [...this.facilitiesAndServices_ar, key_ar];
    }
  }

  addUtility(): void {
    this.utilities_en = [...this.utilities_en, ''];
    this.utilities_ar = [...this.utilities_ar, ''];
  }

  removeUtility(index: number): void {
    this.utilities_en = this.utilities_en.filter((_, i) => i !== index);
    this.utilities_ar = this.utilities_ar.filter((_, i) => i !== index);
  }

  updateUtility(lang: 'ar' | 'en', index: number, value: string): void {
    if (lang === 'ar') {
      this.utilities_ar = [...this.utilities_ar];
      this.utilities_ar[index] = value;
    } else {
      this.utilities_en = [...this.utilities_en];
      this.utilities_en[index] = value;
    }
  }

  addTier(): void {
    this.rentTiers = [...this.rentTiers, { minDays: 1, pricePerDay: null, label: '' }];
  }
  removeTier(i: number): void {
    this.rentTiers = this.rentTiers.filter((_, idx) => idx !== i);
  }

  addPlan(): void {
    this.salePlans = [
      ...this.salePlans,
      {
        name_en: '',
        name_ar: '',
        saleType: 'cash',
        downPaymentPercent: null,
        downPaymentAmount: null,
        years: 0,
        interestRate: 0,
        discountPercent: null,
      },
    ];
  }
  removePlan(i: number): void {
    this.salePlans = this.salePlans.filter((_, idx) => idx !== i);
  }

  onGalleryChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;
    this.galleryFiles = Array.from(input.files);
    this.galleryPreviews = this.galleryFiles.map((f) => URL.createObjectURL(f));
  }

  onDocumentsChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.documentFiles = input.files ? Array.from(input.files) : [];
  }

  async submit(): Promise<void> {
    if (this.isSaving) return;
    this.isSaving = true;
    this.saveError = '';
    this.saveSuccess = '';

    const normalizedFacilitiesEn = this.facilitiesAndServices_en
      .map((item) => item.trim())
      .filter((item) => !!item);
    const normalizedFacilitiesAr = this.facilitiesAndServices_ar
      .map((item) => item.trim())
      .filter((item) => !!item);

    if (normalizedFacilitiesEn.length === 0 || normalizedFacilitiesAr.length === 0) {
      this.saveError = 'يرجى اختيار مرفق واحد على الأقل قبل إنشاء الوحدة.';
      this.step = 'common';
      this.isSaving = false;
      return;
    }

    const normalizedUtilitiesEn = this.utilities_en
      .map((item) => item.trim())
      .filter((item) => !!item);
    const normalizedUtilitiesAr = this.utilities_ar
      .map((item) => item.trim())
      .filter((item) => !!item);
    const normalizedSalePlans = this.salePlans
      .map((plan) => ({
        name_en: (plan.name_en ?? '').trim(),
        name_ar: (plan.name_ar ?? '').trim(),
        saleType: plan.saleType ?? 'cash',
        years: plan.years ?? 0,
        downPaymentPercent: plan.downPaymentPercent ?? null,
        downPaymentAmount: plan.downPaymentAmount ?? null,
        interestRate: plan.interestRate ?? 0,
        discountPercent: plan.discountPercent ?? null,
      }))
      .filter((plan) => !!plan.name_en || !!plan.name_ar || plan.saleType === 'installment');

    const safePlans =
      normalizedSalePlans.length > 0
        ? normalizedSalePlans
        : [
          {
            name_en: 'Cash Plan',
            name_ar: 'خطة كاش',
            saleType: 'cash' as const,
            years: 0,
            downPaymentPercent: null,
            downPaymentAmount: null,
            interestRate: 0,
            discountPercent: null,
          },
        ];

    const unitTypeAr = this.unitTypes.find((t) => t.key === this.unitType_en)?.label_ar ?? '';

    const data = {
      title_en: this.title_en,
      title_ar: this.title_ar,
      description_en: this.description_en,
      description_ar: this.description_ar,
      unitNumber: this.unitNumber,
      unitType_en: this.unitType_en,
      unitType_ar: unitTypeAr,
      purpose: this.purpose,
      buildYear: this.buildYear,
      unitArea: this.unitArea,
      bedrooms: this.bedrooms,
      bathrooms: this.bathrooms,
      floorNumber: this.floorNumber,
      furnished: this.furnished,
      maxGuests: this.maxGuests,
      facilitiesAndServices_en: normalizedFacilitiesEn,
      facilitiesAndServices_ar: normalizedFacilitiesAr,
      utilities_en: normalizedUtilitiesEn,
      utilities_ar: normalizedUtilitiesAr,
      status: this.status,
      projectId: this.projectId,
      location: {
        country: { en: this.country_en, ar: this.country_ar },
        city: { en: this.city_en, ar: this.city_ar },
        area: { en: this.area_en, ar: this.area_ar },
        address: { en: this.address_en, ar: this.address_ar },
        coordinates: {
          type: 'Point',
          coordinates: [this.lng ?? 0, this.lat ?? 0],
        },
      },
    };

    const price: Record<string, unknown> = {};
    if (this.showRent) {
      price['rentPrice'] = {
        rentType: 'daily',
        price: this.rentPrice,
        currency: this.rentCurrency,
        securityDeposit: this.securityDeposit,
        commission: { type: 'percentage', value: 5 },
        tiers: this.rentTiers,
      };
    }
    if (this.showSale) {
      price['salePrice'] = {
        basePrice: this.basePrice,
        pricePerMeter: this.pricePerMeter,
        currency: this.saleCurrency,
        negotiable: this.negotiable,
        commission: { type: 'percentage', value: 5 },
        plans: safePlans,
      };
    }

    try {
      await firstValueFrom(
        this.service.createUnit(data as any, price as any, this.galleryFiles, this.documentFiles),
      );
      this.saveSuccess = 'تم إنشاء الوحدة بنجاح';
      setTimeout(() => this.tabsService.setActiveTab('units'), 1500);
    } catch (err: unknown) {
      const e = err as any;
      const validationErrors = e?.error?.validationErrors;
      if (Array.isArray(validationErrors) && validationErrors.length > 0) {
        const details = validationErrors
          .flatMap((item: any) => (Array.isArray(item?.errors) ? item.errors : []))
          .filter((msg: any) => typeof msg === 'string' && msg.trim().length > 0)
          .map((msg: string) => msg.replace(/^"|"$/g, ''));

        if (details.length > 0) {
          this.saveError = details.join(' | ');
          return;
        }
      }

      this.saveError = e?.error?.message ?? e?.message ?? 'حدث خطأ ما';
    } finally {
      this.isSaving = false;
    }
  }

  cancel(): void {
    this.tabsService.setActiveTab('units');
  }
}
